import { useMemo } from 'react';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { getFeatureAccessLookupKey } from '../utils/staffPermissionBlocks';
import {
  getStaffFeatureAccessRow,
  hasStaffAreaAggregateAccess,
  hasFeaturePermission,
  isEmptyAccessRow,
  type StaffFeatureAccessRow,
} from '../utils/staffFeatureAccessPolicy';
import type { SchoolsResponse } from '../types';

export interface StaffPermissionContext {
  loading: boolean;
  isPrincipal: boolean;
  lookupKey: string | undefined;
  /** Resolved row for this user; undefined if key missing (same as empty for gating). */
  accessRow: StaffFeatureAccessRow | undefined;
  isEmptyRow: boolean;
  hasFeature: (featureId: string) => boolean;
  /** True if user may open any /staff/* route (matches drawer Staff item). */
  hasStaffArea: () => boolean;
}

/**
 * Single `schools` query + derived staff permission row for Layout, routes, and Staff tabs.
 * Prefer this over multiple `useFeatureAccess` calls for the same school row.
 */
export function useStaffPermissionContext(): StaffPermissionContext {
  const user = authService.getStoredUser();
  const isPrincipal = !!user && canAccessPrincipalFeatures(user.role);
  const { data: schoolsData, isLoading } = useQuery<SchoolsResponse>(
    'schools',
    async () => {
      const r = await apiClient.get('/schools');
      return r.data as SchoolsResponse;
    },
    { enabled: !!user },
  );
  const school = schoolsData?.schools?.[0];
  const lookupKey = user ? getFeatureAccessLookupKey(user, school) : undefined;

  return useMemo(() => {
    const accessRow = getStaffFeatureAccessRow(school, lookupKey);
    const empty = isEmptyAccessRow(accessRow);
    return {
      loading: isLoading,
      isPrincipal,
      lookupKey,
      accessRow,
      isEmptyRow: empty,
      hasFeature: (featureId: string) => hasFeaturePermission(accessRow, featureId, isPrincipal),
      hasStaffArea: () => hasStaffAreaAggregateAccess(accessRow, isPrincipal),
    };
  }, [isLoading, isPrincipal, lookupKey, school]);
}
